import { useLocation, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import Header from "@commons/header";
import Column from "@components/column";
import Row from "@components/row";
import Fields from "@components/fields";
import Button from "@components/button";
import { Product } from "@apis/index";
import ButtonWrapper from "@components/wrapper/button-wrapper";
import Modal from "@components/modal";
import Table from "@components/table";

const ViewProduct = () => {
  const { state } = useLocation();
  const { productId } = useParams();
  const [productData, setProductData] = useState<any>(state);
  const [showAddModal, setShowAddModal] = useState(false);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState<any>(null);

  // Fetch product details including recommendations
  const fetchProduct = async () => {
    const id = productData?.id || productId;
    if (id) {
      const res = await Product.find({ id });
      setProductData(res);
    }
  };

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  // Fetch all products for the modal
  const fetchAllProducts = async () => {
    const res = await Product.list({ params: { limit: 1000, isActive: true } });
    setAllProducts(res?.items || []);
    setFilteredProducts(res?.items || []);
  };

  useEffect(() => {
    if (showAddModal) {
      fetchAllProducts();
    }
  }, [showAddModal]);

  // Filter products in modal
  useEffect(() => {
    let filtered = allProducts.filter((p) => p.id !== productData?.id);

    // Filter already recommended products
    const recommendedIds = productData?.recommendations?.map((r: any) => r.id);
    if (recommendedIds?.length) {
      filtered = filtered.filter((p) => !recommendedIds.includes(p.id));
    }

    if (searchTerm) {
      filtered = filtered.filter((p) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredProducts(filtered);
  }, [searchTerm, allProducts, productData]);

  const handleAddRecommendation = async (recommendedProduct: any) => {
    await Product.addRecommendation({
      id: productData.id,
      data: { recommendedProductId: recommendedProduct.id },
    });
    fetchProduct();
    setShowAddModal(false);
  };

  const handleRemoveRecommendation = async (recommendedProduct: any) => {
    await Product.removeRecommendation({
      id: productData.id,
      data: { recommendedProductId: recommendedProduct.id },
    });
    fetchProduct();
  };

  const columns = [
    {
      name: "Image",
      selector: (row: any) => (
        <img
          src={row.images?.[0]?.image}
          alt={row.name}
          style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 8 }}
        />
      ),
    },
    {
      name: "Name",
      selector: (row: any) => row.name,
      sortable: true,
    },
    {
      name: "Price",
      selector: (row: any) => row.price,
      sortable: true,
    },
    {
      name: "Action",
      cell: (row: any) => (
        <Button
          title="Add"
          onClick={() => handleAddRecommendation(row)}
          size="small"
        />
      ),
    },
  ];

  const recommendedColumns = [
    {
      name: "Image",
      selector: (row: any) => (
        <img
          src={row.images?.[0]?.image}
          alt={row.name}
          style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 8 }}
        />
      ),
    },
    {
      name: "Name",
      selector: (row: any) => row.name,
    },
    {
      name: "Action",
      cell: (row: any) => (
        <Button
          title="Remove"
          variant="danger"
          onClick={() => handleRemoveRecommendation(row)}
          size="small"
        />
      ),
    },
  ];

  return (
    <Column stylecolumn="padding: 20px; gap: 20px;">
      <Header title="Product Details" />

      <Row styleRow="width: 100%; gap: 20px; align-items: flex-start;">
        {/* Product Info */}
        <div
          style={{ flex: 1, background: "white", padding: 20, borderRadius: 8 }}
        >
          <h3 style={{ marginBottom: 20 }}>Basic Info</h3>
          <Row styleRow="gap: 20px">
            {productData?.images?.[0]?.image && (
              <img
                src={productData.images[0].image}
                alt={productData.name}
                style={{
                  width: 150,
                  height: 150,
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
            )}
            <Column>
              <p>
                <strong>Name:</strong> {productData?.name}
              </p>
              <p>
                <strong>Price:</strong> {productData?.price}
              </p>
              <p>
                <strong>Category:</strong> {productData?.category?.name}
              </p>
            </Column>
          </Row>
        </div>

        {/* Recommendations */}
        <div
          style={{ flex: 1, background: "white", padding: 20, borderRadius: 8 }}
        >
          <Row styleRow="justify-content: space-between; margin-bottom: 20px;">
            <h3>You May Also Like</h3>
            <Button
              title="Add Recommendation"
              onClick={() => setShowAddModal(true)}
            />
          </Row>

          <Table
            columns={recommendedColumns}
            data={productData?.recommendations || []}
          />
        </div>
      </Row>

      <Modal
        visible={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Recommendation"
        width={800}
      >
        <Column stylecolumn="gap: 20px">
          <Fields.Input
            value={searchTerm}
            onChange={(e: any) => setSearchTerm(e.target.value)}
            placeholder="Search products..."
          />
          <Table
            columns={columns}
            data={filteredProducts}
            pagination
            itemsPerPage={5}
          />
        </Column>
      </Modal>
    </Column>
  );
};

export default ViewProduct;
